import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import HttpManager from "../../../services/HttpManager";
import { axiosInstance } from "../../../services/v2/axiosInstance";
import WappIcono from "../../../components/wappIcono";
import { useFotos } from "../../../appMovil/fotos";
import { avisoLocal } from "../../../appMovil/appMovil";
import { errorTexto, iniciales } from "./salesHelpers";
import { push } from "./push";
import { telefono } from "./telefono/telefono";
import "./llamadasEquipo.css";
import { useArrastre } from "./arrastre";

/**
 * Llamadas de WappTeam como WhatsApp (Esteban, 2026-10-13: "que puedan agregar más gente, que sea bien completo y robusto").
 * WAPP lleva quién llama y a quién le suena (EquipoLlamadas en el servidor); el audio va por el teléfono de WAPP a la sala
 * de la central. Esta pieza se monta UNA vez (app.tsx): pantalla de "te están llamando", pantalla de llamada (cronómetro,
 * silenciar, agregar personas, colgar), y la llamada minimizada para seguir usando WAPP.
 */

export interface PersonaLlamada { userId: number; nombre: string; estado: "sonando" | "conectada" | "rechazo" | "perdida" | "ocupada" | "salio"; }
export interface LlamadaEquipo {
	id: number; sala: string; estado: "sonando" | "activa" | "terminada"; fin: string | null;
	canal: { id: number; tipo: string; nombre: string | null; icono: string | null } | null;
	grupal: boolean; iniciadaPor: number; quienLlama: string; contestadaAt: string | null; ahora: string;
	mio: PersonaLlamada["estado"] | null; conectadas: number; personas: PersonaLlamada[];
	/** Mi audio está en ESTA pestaña (falta en servidores de antes de 2026-10-19: se toma como sí). */
	aqui?: boolean;
}

/**
 * Cada pestaña se presenta con un id propio ("d") para que el audio de la llamada viva en una sola: antes cada pestaña o
 * computadora con WAPP abierto entraba sola a la sala y la persona quedaba dos o tres veces en la llamada ("doble voz",
 * Christy 19-oct). Al recargar la misma pestaña conserva su id (para volver a entrar sola); una pestaña duplicada no.
 */
const PESTANA_RECARGA = "wapp_eqll_pestana";
const pestana: string = (() => {
	try {
		const r = JSON.parse(sessionStorage.getItem(PESTANA_RECARGA) || "null");
		sessionStorage.removeItem(PESTANA_RECARGA);
		if (r && typeof r.d === "string" && Date.now() - Number(r.t) < 30000) return r.d;
	} catch (e) { /* sin sessionStorage: id nuevo */ }
	return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
})();
try { window.addEventListener("pagehide", () => { try { sessionStorage.setItem(PESTANA_RECARGA, JSON.stringify({ d: pestana, t: Date.now() })); } catch (e) { /* nada */ } }); } catch (e) { /* nada */ }
const audioAqui = (l: LlamadaEquipo | null | undefined) => !!l && l.mio === "conectada" && l.aqui !== false;

const base = (c: number) => `/admin/campus/${c}/wappchat`;
const api = {
	mia: (c: number) => axiosInstance.get<{ llamada: LlamadaEquipo | null }>(`${base(c)}/_equipoLlamadas`, { params: { d: pestana } }),
	iniciar: (c: number, canalId: number) => axiosInstance.post<LlamadaEquipo>(`${base(c)}/_equipoLlamadas`, { canalId, d: pestana }),
	estado: (c: number, id: number) => axiosInstance.get<LlamadaEquipo>(`${base(c)}/_equipoLlamada/${id}`, { params: { d: pestana } }),
	accion: (c: number, id: number, accion: string, extra: object = {}) => axiosInstance.post<LlamadaEquipo>(`${base(c)}/_equipoLlamada/${id}`, { accion, d: pestana, ...extra }),
};

// ------------------------------------------------------------------ estado (fuera de React: una sola llamada por pestaña)

interface Estado {
	l: LlamadaEquipo | null;
	campusId: number | null;
	vista: "grande" | "chica";
	audio: "nada" | "conectando" | "listo" | "error";
	error: string | null;
	/** Ya colgué yo (para no reconectar cuando la central cierra la sesión). */
	colgado: boolean;
	desfase: number;   // reloj del servidor - reloj local (ms)
}
let st: Estado = { l: null, campusId: null, vista: "grande", audio: "nada", error: null, colgado: false, desfase: 0 };
const oyentes = new Set<() => void>();
const poner = (cambio: Partial<Estado>) => { st = { ...st, ...cambio }; oyentes.forEach((f) => f()); };
const useEstado = () => useSyncExternalStore((f) => { oyentes.add(f); return () => { oyentes.delete(f); }; }, () => st);

const recibir = (l: LlamadaEquipo | null) => {
	if (l) poner({ l, desfase: new Date(l.ahora).getTime() - Date.now() });
	else poner({ l: null });
};

/** El teléfono de WAPP saca sus credenciales del plantel actual (también sin CRM: WappTeam llama). */
const prepararTelefono = (campusId: number) => {
	// Si ya tiene el teléfono de clientes conectado, se usa ese. Si no, su extensión o, sin extensión, la entrada compartida
	// de WappTeam (conecta al instante y no gasta extensiones de la central).
	if (telefono.creds && telefono.creds.registrar !== false) return;
	telefono.usarFuente(async () => {
		const r = await axiosInstance.get(`/admin/campus/${campusId}/prospects/_pbx`, { params: { wappteam: 1 } });
		return r.data.enabled ? r.data : null;
	});
};

let reintentosAudio = 0;
/** Entra a la sala de la llamada con el teléfono de WAPP. La primera vez de alguien la central tarda hasta un minuto en conocer su extensión. */
const conectarAudio = async () => {
	const { l, campusId } = st;
	if (!l || !campusId || l.estado === "terminada" || !audioAqui(l)) return;
	if (telefono.llamada?.equipo === l.id && telefono.ocupado) { poner({ audio: "listo" }); return; }
	if (telefono.ocupado) { poner({ audio: "error", error: "Cuelga la llamada del teléfono de clientes para entrar." }); return; }
	poner({ audio: "conectando", error: null });
	prepararTelefono(campusId);
	const id = await telefono.llamarYa(l.sala, { name: l.canal?.nombre || l.quienLlama || "WappTeam", equipo: l.id }, 20000);
	if (!st.l || st.l.id !== l.id) { if (id) telefono.colgar(); return; }
	// Mientras marcaba, el audio pasó a otra pestaña: suelta la sala para no quedar dos veces.
	if (!audioAqui(st.l)) { if (id) { poner({ colgado: true }); telefono.colgar(); } poner({ audio: "nada" }); return; }
	if (!id) {
		poner({ audio: "error", error: telefono.error || "No se pudo conectar el audio (revisa el permiso del micrófono)." });
		return;
	}
	reintentosAudio = 0;
	poner({ audio: "listo" });
};

// La central cerró el audio sin que yo colgara (se cayó el internet, la pestaña durmió): se reconecta dos veces y si no, se sale.
telefono.alTerminar((f) => {
	if (!f.equipo || !st.l || st.l.id !== f.equipo || st.colgado || st.l.estado === "terminada") return;
	if (reintentosAudio < 2) { reintentosAudio++; poner({ audio: "conectando" }); setTimeout(conectarAudio, 1500); return; }
	colgarLlamada();
});

/** Llamar desde una conversación (botón 📞). En un grupo con llamada viva, entra a esa. */
export const llamarEnEquipo = async (campusId: number, canalId: number): Promise<string | null> => {
	if (st.l && st.l.estado !== "terminada") return "Ya estás en una llamada.";
	if (telefono.ocupado) return "Estás en una llamada del teléfono de clientes.";
	try {
		telefono["ctxAudio"]?.call(telefono);   // el audio nace con el clic (iPhone)
		poner({ campusId, colgado: false, vista: "grande", error: null, audio: "nada" });
		const r = await api.iniciar(campusId, canalId);
		recibir(r.data);
		conectarAudio();
		return null;
	} catch (e) { poner({ l: null }); return errorTexto(e); }
};

/** "Unirme" a la llamada en curso de un grupo. */
export const unirmeALlamada = async (campusId: number, llamadaId: number): Promise<string | null> => {
	if (st.l && st.l.id !== llamadaId && st.l.estado !== "terminada") return "Ya estás en otra llamada.";
	if (telefono.ocupado && telefono.llamada?.equipo !== llamadaId) return "Estás en una llamada del teléfono de clientes.";
	try {
		poner({ campusId, colgado: false, vista: "grande", error: null });
		const r = await api.accion(campusId, llamadaId, "contestar");
		recibir(r.data);
		conectarAudio();
		return null;
	} catch (e) { return errorTexto(e); }
};

const contestar = async () => {
	const { l, campusId } = st;
	if (!l || !campusId) return;
	poner({ colgado: false, vista: "grande" });
	try {
		const r = await api.accion(campusId, l.id, "contestar");
		recibir(r.data);
		conectarAudio();
	} catch (e) { poner({ error: errorTexto(e) }); setTimeout(() => poner({ l: null, error: null }), 3000); }
};

/** "Pasar Aquí": el audio se muda a esta pestaña; la otra cuelga el suyo sola al enterarse. */
const pasarAqui = async () => {
	const { l, campusId } = st;
	if (!l || !campusId) return;
	if (telefono.ocupado && telefono.llamada?.equipo !== l.id) { poner({ error: "Cuelga la llamada del teléfono de clientes para pasarla aquí." }); return; }
	telefono["ctxAudio"]?.call(telefono);   // el audio nace con el clic (iPhone)
	poner({ colgado: false, vista: "grande", error: null });
	try {
		recibir((await api.accion(campusId, l.id, "pasar")).data);
		reintentosAudio = 0;
		conectarAudio();
	} catch (e) { poner({ error: errorTexto(e) }); }
};

const rechazar = async (ocupada = false) => {
	const { l, campusId } = st;
	if (!l || !campusId) return;
	poner({ l: null });
	api.accion(campusId, l.id, ocupada ? "ocupada" : "rechazar").catch(() => null);
};

const colgarLlamada = async () => {
	const { l, campusId } = st;
	poner({ colgado: true });
	if (telefono.llamada?.equipo) telefono.colgar();
	if (!l || !campusId) { poner({ l: null }); return; }
	try { recibir((await api.accion(campusId, l.id, "colgar")).data); } catch { poner({ l: null }); }
};

// ------------------------------------------------------------------ sonidos

let ctx: AudioContext | null = null;
let tono: { parar: () => void; tipo: string } | null = null;
const sonido = (tipo: "entrante" | "llamando" | null) => {
	if (tono && tono.tipo === tipo) return;
	tono?.parar();
	tono = null;
	if (!tipo) return;
	try {
		const C = (window as any).AudioContext || (window as any).webkitAudioContext;
		ctx = ctx && ctx.state !== "closed" ? ctx : new C();
		ctx!.resume?.();
		const c = ctx!;
		const g = c.createGain(); g.gain.value = 0; g.connect(c.destination);
		const o1 = c.createOscillator(); const o2 = c.createOscillator();
		o1.frequency.value = tipo === "entrante" ? 660 : 425; o2.frequency.value = tipo === "entrante" ? 880 : 425;
		o1.connect(g); o2.connect(g); o1.start(); o2.start();
		// Entrante: dos tonos alegres cada 2.5 s. Llamando: el timbre de Latinoamérica (1 s sí, 4 s no).
		const pulso = () => {
			const t = c.currentTime;
			if (tipo === "entrante") {
				g.gain.setValueAtTime(0.12, t); g.gain.setValueAtTime(0, t + 0.35); g.gain.setValueAtTime(0.12, t + 0.5); g.gain.setValueAtTime(0, t + 0.85);
			} else {
				g.gain.setValueAtTime(0.06, t); g.gain.setValueAtTime(0, t + 1);
			}
		};
		pulso();
		const iv = setInterval(pulso, tipo === "entrante" ? 2500 : 5000);
		tono = { tipo, parar: () => { clearInterval(iv); try { o1.stop(); o2.stop(); g.disconnect(); } catch { /* ya */ } } };
	} catch { /* sin sonido: queda la pantalla */ }
};

// ------------------------------------------------------------------ piezas de pantalla

const Avatar: React.FC<{ userId?: number; nombre: string; grande?: boolean; grupo?: boolean; icono?: string | null }> = ({ userId, nombre, grande, grupo, icono }) => {
	const fotos = useFotos();
	const foto = userId ? fotos[userId] : null;
	const [n, a] = nombre.split(" ");
	return (
		<span className={"lleq-av" + (grande ? " grande" : "")} aria-hidden="true">
			{grupo ? (icono ? <span className="lleq-emoji">{icono}</span> : <WappIcono n="users" />) : foto ? <img src={foto} alt="" /> : iniciales({ first_name: n || "", last_name: a || "" })}
		</span>
	);
};

const ESTADO_PERSONA: Record<string, string> = { sonando: "Sonando…", conectada: "En la llamada", rechazo: "Rechazó", perdida: "No contestó", ocupada: "En otra llamada", salio: "Salió" };
const FIN: Record<string, string> = { rechazada: "Rechazó la llamada", ocupada: "Está en otra llamada", sin_respuesta: "No contestó", cancelada: "Llamada cancelada", contestada: "Llamada terminada" };

const reloj = (desde: string | null, desfase: number): string => {
	if (!desde) return "";
	const s = Math.max(0, Math.floor((Date.now() + desfase - new Date(desde).getTime()) / 1000));
	const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
	return (h ? h + ":" + String(m).padStart(2, "0") : String(m)) + ":" + String(x).padStart(2, "0");
};

/** Buscar y agregar personas a la llamada. */
const Agregar: React.FC<{ campusId: number; l: LlamadaEquipo; onCerrar: () => void }> = ({ campusId, l, onCerrar }) => {
	const [q, setQ] = useState("");
	const [res, setRes] = useState<{ id: number; nombre: string; rol: string | null }[]>([]);
	const [elegidas, setElegidas] = useState<number[]>([]);
	const [ocupado, setOcupado] = useState(false);
	const [err, setErr] = useState<string | null>(null);
	// Sin lista gigante: resultados solo al escribir (Esteban, 2026-10-13: "elegir de una lista de 300 personas is dumb").
	useEffect(() => {
		if (q.trim().length < 2) { setRes([]); return; }
		const t = setTimeout(() => {
			HttpManager.getInstance().prospectsService.equipoPersonas(campusId, q.trim())
				.then((r) => setRes(r.data.personas.filter((p) => !l.personas.some((x) => x.userId === p.id && (x.estado === "conectada" || x.estado === "sonando")))))
				.catch(() => setRes([]));
		}, 250);
		return () => clearTimeout(t);
	}, [q]);
	const agregar = async () => {
		setOcupado(true); setErr(null);
		try { recibir((await api.accion(campusId, l.id, "agregar", { userIds: elegidas })).data); onCerrar(); }
		catch (e) { setErr(errorTexto(e)); } finally { setOcupado(false); }
	};
	return (
		<div className="lleq-agregar" role="dialog" aria-label="Agregar Personas">
			<div className="lleq-agregar-cab"><b>Agregar Personas</b><button type="button" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button></div>
			<input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o rol" />
			<div className="lleq-agregar-lista">
				{res.map((p) => (
					<label key={p.id} className={elegidas.includes(p.id) ? "on" : ""}>
						<input type="checkbox" checked={elegidas.includes(p.id)} onChange={(e) => setElegidas(e.target.checked ? [...elegidas, p.id] : elegidas.filter((x) => x !== p.id))} />
						<Avatar userId={p.id} nombre={p.nombre} />
						<span><b>{p.nombre}</b>{p.rol ? <small>{p.rol}</small> : null}</span>
					</label>
				))}
				{!res.length ? <span className="lleq-tenue">{q.trim().length >= 2 ? "Nadie con ese nombre." : "Escribe el nombre de quien quieres agregar."}</span> : null}
			</div>
			{err ? <div className="lleq-err">{err}</div> : null}
			<button type="button" className="lleq-agregar-ok" disabled={!elegidas.length || ocupado} onClick={agregar}>
				{ocupado ? "Llamando…" : "Llamar" + (elegidas.length ? " (" + elegidas.length + ")" : "")}
			</button>
		</div>
	);
};

// ------------------------------------------------------------------ la pieza global

export const LlamadasEquipo: React.FC = () => {
	const e = useEstado();
	const navigate = useNavigate();
	const sessionCampus: any = useSelector((s: any) => s.rootReducer?.sessionState?.currentCampus);
	const legacyKey: any = useSelector((s: any) => s.currentUserCampus?.value);
	const legacyRef: any = useSelector((s: any) => s.campusRef?.value);
	const userId: number | null = useSelector((s: any) => s.rootReducer?.sessionState?.user?.id ?? null);
	const campusId: number | null = (legacyKey && legacyRef ? legacyRef[legacyKey]?.id : null) ?? sessionCampus?.id ?? null;
	const [, tick] = useState(0);
	const [agregando, setAgregando] = useState(false);
	const tel = useSyncExternalStore((f) => telefono.suscribir(f), () => telefono.llamada?.muted ?? false);
	const finVisto = useRef<number | null>(null);
	const arrastrePildora = useArrastre("lleq-pildora", "arriba-centro");

	useEffect(() => { if (campusId && !st.campusId) poner({ campusId }); }, [campusId]);

	// ¿Hay algo pendiente? Al abrir WAPP, al reconectar los avisos al instante, al volver a la pestaña y con ?llamada= (aviso del celular).
	useEffect(() => {
		if (!campusId) return;
		const revisar = () => {
			if (st.l && st.l.estado !== "terminada") return;
			api.mia(campusId).then((r) => {
				if (r.data.llamada && (!st.l || st.l.estado === "terminada")) { poner({ campusId }); recibir(r.data.llamada); if (audioAqui(r.data.llamada)) conectarAudio(); }
			}).catch(() => null);
		};
		revisar();
		const p = new URLSearchParams(window.location.search);
		if (p.get("llamada")) window.history.replaceState(null, "", window.location.pathname);
		const fuera = push.on("llamada", (d: any) => {
			if (st.l && d.l === st.l.id) { api.estado(campusId, d.l).then((r) => recibir(r.data)).catch(() => null); return; }
			if (d.a === "timbre") revisar();
		});
		const vis = () => { if (document.visibilityState === "visible") revisar(); };
		document.addEventListener("visibilitychange", vis);
		// Respaldo: sin avisos al instante (se cayó la conexión), pregunta cada 15 s con la pestaña a la vista.
		const respaldo = setInterval(() => { if (!push.conectado && document.visibilityState === "visible") revisar(); }, 15000);
		return () => { fuera(); clearInterval(respaldo); document.removeEventListener("visibilitychange", vis); };
	}, [campusId]);

	// Mientras hay llamada: estado cada 4 s (y "sigo aquí" si estoy conectada). Robusto aunque se pierdan avisos.
	const lid = e.l?.id;
	const vivo = !!e.l && e.l.estado !== "terminada";
	useEffect(() => {
		if (!lid || !vivo || !st.campusId) return;
		const c = st.campusId;
		const t = setInterval(() => {
			const l = st.l;
			if (!l || l.id !== lid) return;
			(audioAqui(l) ? api.accion(c, lid, "latido") : api.estado(c, lid)).then((r) => { if (st.l?.id === lid) recibir(r.data); }).catch((x) => {
				if (x?.response?.status === 404 || x?.response?.status === 403) poner({ l: null });
			});
		}, 4000);
		const r = setInterval(() => tick((x) => x + 1), 1000);
		return () => { clearInterval(t); clearInterval(r); };
	}, [lid, vivo]);

	const l = e.l;
	const entrante = !!l && l.estado !== "terminada" && l.mio === "sonando";
	const enLlamada = !!l && l.estado !== "terminada" && l.mio === "conectada";
	const enOtra = enLlamada && !audioAqui(l);
	const otras = l ? l.personas.filter((p) => p.userId !== userId) : [];
	const alguienMas = otras.some((p) => p.estado === "conectada");

	// El audio pasó a otra pestaña o computadora (contestó o dio "Pasar Aquí" allá): esta suelta la sala.
	useEffect(() => {
		if (!enOtra || !l) return;
		if (telefono.llamada?.equipo === l.id) { poner({ colgado: true }); telefono.colgar(); }
		poner({ audio: "nada", error: null });
	}, [enOtra, l?.id]);

	// Ocupada en el teléfono de clientes: WAPP contesta "en otra llamada" sola (como WhatsApp).
	useEffect(() => { if (entrante && telefono.ocupado && !telefono.llamada?.equipo) rechazar(true); }, [entrante]);

	// Sonidos y aviso del sistema.
	useEffect(() => {
		sonido(entrante ? "entrante" : enLlamada && !enOtra && !alguienMas && l?.estado === "sonando" ? "llamando" : null);
		return undefined;
	}, [entrante, enLlamada, enOtra, alguienMas, l?.estado]);
	useEffect(() => () => sonido(null), []);
	useEffect(() => {
		if (!entrante || !l || !document.hidden) return;
		avisoLocal("📞 Llamada de WappTeam", (l.quienLlama || "Una compañera") + (l.grupal && l.canal?.nombre ? " · " + l.canal.nombre : " te está llamando"), "llamada-eq-" + l.id, "/wappteam?llamada=" + l.id);
	}, [entrante, l?.id]);

	// Al terminar: el teléfono cuelga y la pantalla dice por qué unos segundos.
	useEffect(() => {
		if (!l || l.estado !== "terminada" || finVisto.current === l.id) return;
		finVisto.current = l.id;
		if (telefono.llamada?.equipo === l.id) { poner({ colgado: true }); telefono.colgar(); }
		const id = l.id;
		setTimeout(() => { if (st.l?.id === id) poner({ l: null, audio: "nada", error: null }); }, l.mio === "sonando" || l.mio === "perdida" ? 0 : 2500);
	}, [l?.estado, l?.id]);

	if (!l || !campusId) return null;
	if (l.estado === "terminada" && (l.mio === "perdida" || l.mio === "rechazo" || l.mio === "ocupada")) return null;

	const titulo = l.grupal ? (l.canal?.nombre || "Llamada grupal") : (otras[0]?.nombre || l.quienLlama);
	const quien = l.grupal ? null : otras[0];

	if (entrante) {
		return (
			<div className="lleq-entrante" role="alertdialog" aria-label={"Llamada de " + l.quienLlama}>
				<Avatar userId={l.iniciadaPor} nombre={l.quienLlama} grande grupo={l.grupal && !!l.canal && l.canal.tipo !== "directo"} icono={l.canal?.icono} />
				<div className="lleq-entrante-tx">
					<b>{l.grupal && l.canal?.tipo !== "directo" ? l.canal?.nombre || "Llamada grupal" : l.quienLlama}</b>
					<span>{l.grupal ? (l.quienLlama + " te invita a una llamada grupal") : "Llamada de voz de WappTeam"}</span>
				</div>
				<div className="lleq-entrante-acc">
					<button type="button" className="lleq-red colgar" onClick={() => rechazar()} aria-label="Rechazar"><WappIcono n="phoneOff" /><small>Rechazar</small></button>
					<button type="button" className="lleq-red contestar" onClick={contestar} aria-label="Contestar"><WappIcono n="phone" /><small>Contestar</small></button>
				</div>
			</div>
		);
	}

	if (enOtra) {
		return (
			<div className="lleq-otra" role="status" aria-label={"Llamada con " + titulo + " en otra pantalla"}>
				<WappIcono n="phone" />
				<span className="lleq-otra-tx"><b>{titulo}</b><small>{e.error || "Sigue en otra pantalla"}</small></span>
				<button type="button" className="lleq-otra-pasar" onClick={pasarAqui} title="Escuchar y hablar desde aquí (la otra pantalla suelta el audio)">Pasar Aquí</button>
				<button type="button" className="lleq-otra-colgar" onClick={colgarLlamada} aria-label="Colgar" title="Colgar"><WappIcono n="phoneOff" /></button>
			</div>
		);
	}

	const estadoTexto = l.estado === "terminada" ? (FIN[l.fin || ""] || "Llamada terminada")
		: e.audio === "conectando" ? "Conectando audio…"
		: e.audio === "error" ? (e.error || "Sin audio")
		: !alguienMas ? (l.estado === "sonando" ? (otras.some((p) => p.estado === "sonando") ? "Llamando…" : "Esperando…") : "Esperando a que entren…")
		: reloj(l.contestadaAt, e.desfase);

	if (e.vista === "chica") {
		return (
			<button type="button" className="lleq-pildora" onClick={() => poner({ vista: "grande" })} aria-label="Volver a la llamada"
				ref={arrastrePildora.ref} style={arrastrePildora.style} {...arrastrePildora.agarre} title="Clic para volver · arrastra para moverla">
				<WappIcono n="phone" /><b>{titulo}</b><span>{estadoTexto}</span>
			</button>
		);
	}

	return (
		<div className="lleq-pantalla" role="dialog" aria-label={"Llamada con " + titulo}>
			<div className="lleq-cab">
				<button type="button" className="lleq-ico" onClick={() => poner({ vista: "chica" })} aria-label="Minimizar" title="Seguir usando WAPP"><WappIcono n="minus" /></button>
				<span className="lleq-cifrado">Llamada de WappTeam</span>
				{l.canal ? <button type="button" className="lleq-ico" onClick={() => { poner({ vista: "chica" }); navigate("/wappteam?c=" + l.canal!.id); }} aria-label="Abrir la conversación" title="Abrir la conversación"><WappIcono n="message" /></button> : <span />}
			</div>
			<div className="lleq-centro">
				{quien ? <Avatar userId={quien.userId} nombre={quien.nombre} grande /> : <Avatar nombre={titulo} grande grupo icono={l.canal?.icono} />}
				<b className="lleq-titulo">{titulo}</b>
				<span className={"lleq-estado" + (e.audio === "error" ? " mal" : "")}>{estadoTexto}</span>
				{l.grupal || otras.length > 1 ? (
					<ul className="lleq-personas">
						{l.personas.filter((p) => p.estado !== "salio" || p.userId !== userId).map((p) => (
							<li key={p.userId} className={"e-" + p.estado}>
								<Avatar userId={p.userId} nombre={p.nombre} />
								<span><b>{p.userId === userId ? "Tú" : p.nombre}</b><small>{ESTADO_PERSONA[p.estado]}</small></span>
							</li>
						))}
					</ul>
				) : null}
				{e.audio === "error" && l.estado !== "terminada" ? <button type="button" className="lleq-reintentar" onClick={conectarAudio}>Reintentar Audio</button> : null}
			</div>
			{l.estado !== "terminada" ? (
				<div className="lleq-acc">
					<button type="button" className={"lleq-red" + (tel ? " on" : "")} onClick={() => telefono.silenciar()} disabled={e.audio !== "listo"} aria-pressed={tel} aria-label={tel ? "Quitar Silencio" : "Silenciar"}>
						<WappIcono n={tel ? "micOff" : "mic"} /><small>{tel ? "Sin micrófono" : "Silenciar"}</small></button>
					<button type="button" className="lleq-red" onClick={() => setAgregando(true)} aria-label="Agregar Persona"><WappIcono n="plus" /><small>Agregar</small></button>
					<button type="button" className="lleq-red colgar" onClick={colgarLlamada} aria-label="Colgar"><WappIcono n="phoneOff" /><small>Colgar</small></button>
				</div>
			) : null}
			{agregando ? <Agregar campusId={campusId} l={l} onCerrar={() => setAgregando(false)} /> : null}
		</div>
	);
};

export default LlamadasEquipo;

// ------------------------------------------------------------------ piezas para WappTeam (pantalla completa y ventanitas)

/** Botón 📞 del encabezado de la conversación. */
export const BotonLlamar: React.FC<{ campusId: number; canalId: number; className: string; onError: (e: string | null) => void; conTexto?: boolean }> = ({ campusId, canalId, className, onError, conTexto }) => {
	const [ocupado, setOcupado] = useState(false);
	return (
		<button type="button" className={className} disabled={ocupado} title="Llamar" aria-label="Llamar"
			onClick={async () => { setOcupado(true); onError(await llamarEnEquipo(campusId, canalId)); setOcupado(false); }}>
			{conTexto ? <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" /></svg>
				: <WappIcono n="phone" />}{conTexto ? <span className="solo-pc">Llamar</span> : null}
		</button>
	);
};

/** "Llamada en curso · Unirme" arriba de la conversación de un grupo. */
export const LlamadaViva: React.FC<{ campusId: number; llamada: { id: number; conectadas: number } | null | undefined; onError: (e: string | null) => void }> = ({ campusId, llamada, onError }) => {
	const e = useEstado();
	if (!llamada || (e.l && e.l.id === llamada.id && e.l.mio === "conectada")) return null;
	return (
		<div className="eq-llamada-viva" role="status">
			<WappIcono n="phone" /><span>Llamada en curso · {llamada.conectadas} {llamada.conectadas === 1 ? "persona" : "personas"}</span>
			<button type="button" onClick={async () => onError(await unirmeALlamada(campusId, llamada.id))}>Unirme</button>
		</div>
	);
};

/** Mensajes de llamada que deja el servidor ("📞 Llamada perdida", "📞 Llamada de voz · 3:12"): burbuja como WhatsApp. */
export const esMensajeLlamada = (body: string | null | undefined) => !!body && body.startsWith("📞 ");

export const BurbujaLlamada: React.FC<{ campusId: number; canalId: number; body: string; hora?: string; directo: boolean; viva?: { id: number } | null; onError: (e: string | null) => void }> =
	({ campusId, canalId, body, directo, viva, onError }) => {
	const texto = body.replace(/^📞\s*/, "");
	const enCurso = texto.startsWith("Llamada grupal en curso");
	const perdida = /perdida|rechazada|no contestada|sin respuesta/i.test(texto);
	const [titulo, ...resto] = texto.split(" · ");
	return (
		<span className="eq-llamada-burbuja">
			<span className={"ico" + (perdida ? " perdida" : "")}><WappIcono n={perdida ? "phoneOff" : "phone"} /></span>
			<span className="tx"><b>{enCurso ? "Llamada grupal" : titulo}</b>{enCurso ? <small>{viva ? "En curso" : "Terminó"}</small> : resto.length ? <small>{resto.join(" · ")}</small> : null}</span>
			{enCurso ? (viva ? <button type="button" onClick={async () => onError(await unirmeALlamada(campusId, viva.id))}>Unirme</button> : null)
				: <button type="button" onClick={async () => onError(await llamarEnEquipo(campusId, canalId))}>{directo ? "Volver a Llamar" : "Llamar"}</button>}
		</span>
	);
};
